import {
  Activity,
  Archive,
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  ArrowUpRight,
  Bell,
  Braces,
  Camera,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  CircleAlert,
  CircleCheck,
  CirclePause,
  CircleX,
  Clock,
  Code,
  Copy,
  Crop,
  Download,
  Ellipsis,
  Eye,
  EyeOff,
  FileText,
  FlaskConical,
  FlipHorizontal2,
  FlipVertical2,
  Folder,
  FolderPlus,
  Frame,
  Globe,
  GripVertical,
  History,
  Image,
  Inbox,
  Info,
  LayoutTemplate,
  Lightbulb,
  Link,
  List,
  LoaderCircle,
  Lock,
  type LucideIcon,
  type LucideProps,
  Mail,
  MessageSquare,
  MessageSquarePlus,
  Mic,
  Monitor,
  Moon,
  Newspaper,
  Palette,
  Paperclip,
  Pause,
  Pencil,
  Play,
  Plug,
  Plus,
  Reply,
  RotateCcw,
  RotateCw,
  Search,
  Send,
  ShieldCheck,
  Smartphone,
  Sparkle,
  Tag,
  Terminal,
  ThumbsDown,
  ThumbsUp,
  Trash2,
  TriangleAlert,
  Type,
  Upload,
  User,
  Users,
  Workflow,
  Wrench,
  X,
  Zap,
} from 'lucide-react'
import { createElement, type ReactNode } from 'react'

/**
 * The chat icon seam. Every consumer imports from `@/components/chat/icons`
 * and renders `<XIcon size strokeWidth className />`; the glyphs behind the
 * seam are lucide's, adapted to the chat type scale.
 *
 * `size` is the rendered width and height in px (default 14, drawn for the
 * 13px chat type scale); `strokeWidth` is in SCREEN px (default 1.5).
 * lucide's `absoluteStrokeWidth` does the viewBox maths so a 1.5px stroke
 * stays 1.5px at every size instead of scaling with the glyph.
 */
export type ChatIconProps = {
  size?: number
  strokeWidth?: number
  className?: string
}

export type ChatIcon = (props: ChatIconProps) => ReactNode

export const CHAT_ICON_SIZE = 14
export const CHAT_ICON_STROKE_WIDTH = 1.5

/** Decorative by default (`aria-hidden`), like the hand-drawn set was. */
function chatGlyph(
  Glyph: LucideIcon,
  {
    size = CHAT_ICON_SIZE,
    strokeWidth = CHAT_ICON_STROKE_WIDTH,
    className,
  }: ChatIconProps,
  extra?: Pick<LucideProps, 'fill' | 'stroke'>
): ReactNode {
  return createElement(Glyph, {
    size,
    strokeWidth,
    absoluteStrokeWidth: true,
    className,
    'aria-hidden': true,
    ...extra,
  })
}

/** The sparkle is a FILLED four-point star, not a stroked outline. */
const FILLED_GLYPH: Pick<LucideProps, 'fill' | 'stroke'> = {
  fill: 'currentColor',
  stroke: 'none',
}

export function ArrowDownIcon(props: ChatIconProps) {
  return chatGlyph(ArrowDown, props)
}

export function ArrowUpIcon(props: ChatIconProps) {
  return chatGlyph(ArrowUp, props)
}

/** Sortable-but-unsorted column marker: the up + down pair. */
export function ArrowUpDownIcon(props: ChatIconProps) {
  return chatGlyph(ArrowUpDown, props)
}

export function ArrowUpRightIcon(props: ChatIconProps) {
  return chatGlyph(ArrowUpRight, props)
}

/** Curly braces: merge tags. */
export function BracesIcon(props: ChatIconProps) {
  return chatGlyph(Braces, props)
}

export function CameraIcon(props: ChatIconProps) {
  return chatGlyph(Camera, props)
}

export function ChartBarsIcon(props: ChatIconProps) {
  return chatGlyph(ChartColumn, props)
}

export function CheckIcon(props: ChatIconProps) {
  return chatGlyph(Check, props)
}

export function ChevronDownIcon(props: ChatIconProps) {
  return chatGlyph(ChevronDown, props)
}

export function ChevronLeftIcon(props: ChatIconProps) {
  return chatGlyph(ChevronLeft, props)
}

export function ChevronRightIcon(props: ChatIconProps) {
  return chatGlyph(ChevronRight, props)
}

export function CircleCheckIcon(props: ChatIconProps) {
  return chatGlyph(CircleCheck, props)
}

export function CirclePauseIcon(props: ChatIconProps) {
  return chatGlyph(CirclePause, props)
}

export function CircleXIcon(props: ChatIconProps) {
  return chatGlyph(CircleX, props)
}

export function ClockIcon(props: ChatIconProps) {
  return chatGlyph(Clock, props)
}

export function CodeIcon(props: ChatIconProps) {
  return chatGlyph(Code, props)
}

export function CopyIcon(props: ChatIconProps) {
  return chatGlyph(Copy, props)
}

export function CropIcon(props: ChatIconProps) {
  return chatGlyph(Crop, props)
}

export function DownloadIcon(props: ChatIconProps) {
  return chatGlyph(Download, props)
}

export function EyeIcon(props: ChatIconProps) {
  return chatGlyph(Eye, props)
}

export function EyeOffIcon(props: ChatIconProps) {
  return chatGlyph(EyeOff, props)
}

export function MoreHorizontalIcon(props: ChatIconProps) {
  return chatGlyph(Ellipsis, props)
}

export function FileTextIcon(props: ChatIconProps) {
  return chatGlyph(FileText, props)
}

export function FolderPlusIcon(props: ChatIconProps) {
  return chatGlyph(FolderPlus, props)
}

export function PaperclipIcon(props: ChatIconProps) {
  return chatGlyph(Paperclip, props)
}

export function FlipHorizontalIcon(props: ChatIconProps) {
  return chatGlyph(FlipHorizontal2, props)
}

export function FlipVerticalIcon(props: ChatIconProps) {
  return chatGlyph(FlipVertical2, props)
}

export function FrameIcon(props: ChatIconProps) {
  return chatGlyph(Frame, props)
}

export function GlobeIcon(props: ChatIconProps) {
  return chatGlyph(Globe, props)
}

/** A drag handle: grab it to reorder a row. */
export function GripIcon(props: ChatIconProps) {
  return chatGlyph(GripVertical, props)
}

export function ImageIcon(props: ChatIconProps) {
  return chatGlyph(Image, props)
}

export function LinkIcon(props: ChatIconProps) {
  return chatGlyph(Link, props)
}

/** Pair with `animate-spin` for an in-flight action. */
export function LoaderIcon(props: ChatIconProps) {
  return chatGlyph(LoaderCircle, props)
}

export function MailIcon(props: ChatIconProps) {
  return chatGlyph(Mail, props)
}

/** Composer dictation toggle. */
export function MicIcon(props: ChatIconProps) {
  return chatGlyph(Mic, props)
}

export function MessageSquareIcon(props: ChatIconProps) {
  return chatGlyph(MessageSquare, props)
}

export function MessageSquarePlusIcon(props: ChatIconProps) {
  return chatGlyph(MessageSquarePlus, props)
}

export function MonitorIcon(props: ChatIconProps) {
  return chatGlyph(Monitor, props)
}

export function NewspaperIcon(props: ChatIconProps) {
  return chatGlyph(Newspaper, props)
}

export function PaletteIcon(props: ChatIconProps) {
  return chatGlyph(Palette, props)
}

export function PencilIcon(props: ChatIconProps) {
  return chatGlyph(Pencil, props)
}

export function PlusIcon(props: ChatIconProps) {
  return chatGlyph(Plus, props)
}

export function ReplyArrowIcon(props: ChatIconProps) {
  return chatGlyph(Reply, props)
}

export function RetryIcon(props: ChatIconProps) {
  return chatGlyph(RotateCw, props)
}

export function RotateLeftIcon(props: ChatIconProps) {
  return chatGlyph(RotateCcw, props)
}

export function RotateRightIcon(props: ChatIconProps) {
  return chatGlyph(RotateCw, props)
}

export function SearchIcon(props: ChatIconProps) {
  return chatGlyph(Search, props)
}

export function SendIcon(props: ChatIconProps) {
  return chatGlyph(Send, props)
}

export function SparkleIcon(props: ChatIconProps) {
  return chatGlyph(Sparkle, props, FILLED_GLYPH)
}

export function TagIcon(props: ChatIconProps) {
  return chatGlyph(Tag, props)
}

export function TerminalIcon(props: ChatIconProps) {
  return chatGlyph(Terminal, props)
}

export function ThumbsDownIcon(props: ChatIconProps) {
  return chatGlyph(ThumbsDown, props)
}

export function ThumbsUpIcon(props: ChatIconProps) {
  return chatGlyph(ThumbsUp, props)
}

export function TypeGlyphIcon(props: ChatIconProps) {
  return chatGlyph(Type, props)
}

export function UsersIcon(props: ChatIconProps) {
  return chatGlyph(Users, props)
}

export function WorkflowIcon(props: ChatIconProps) {
  return chatGlyph(Workflow, props)
}

export function WrenchIcon(props: ChatIconProps) {
  return chatGlyph(Wrench, props)
}

export function XMarkIcon(props: ChatIconProps) {
  return chatGlyph(X, props)
}

export function PlayIcon(props: ChatIconProps) {
  return chatGlyph(Play, props)
}

export function ActivityIcon(props: ChatIconProps) {
  return chatGlyph(Activity, props)
}

export function ArchiveIcon(props: ChatIconProps) {
  return chatGlyph(Archive, props)
}

export function CircleAlertIcon(props: ChatIconProps) {
  return chatGlyph(CircleAlert, props)
}

export function FlaskIcon(props: ChatIconProps) {
  return chatGlyph(FlaskConical, props)
}

export function FolderIcon(props: ChatIconProps) {
  return chatGlyph(Folder, props)
}

export function HistoryIcon(props: ChatIconProps) {
  return chatGlyph(History, props)
}

export function InboxIcon(props: ChatIconProps) {
  return chatGlyph(Inbox, props)
}

export function LayoutTemplateIcon(props: ChatIconProps) {
  return chatGlyph(LayoutTemplate, props)
}

export function LightbulbIcon(props: ChatIconProps) {
  return chatGlyph(Lightbulb, props)
}

export function ListIcon(props: ChatIconProps) {
  return chatGlyph(List, props)
}

export function MoonIcon(props: ChatIconProps) {
  return chatGlyph(Moon, props)
}

export function PauseIcon(props: ChatIconProps) {
  return chatGlyph(Pause, props)
}

export function PlugIcon(props: ChatIconProps) {
  return chatGlyph(Plug, props)
}

export function ShieldCheckIcon(props: ChatIconProps) {
  return chatGlyph(ShieldCheck, props)
}

export function SmartphoneIcon(props: ChatIconProps) {
  return chatGlyph(Smartphone, props)
}

export function TrashIcon(props: ChatIconProps) {
  return chatGlyph(Trash2, props)
}

export function TriangleAlertIcon(props: ChatIconProps) {
  return chatGlyph(TriangleAlert, props)
}

export function UserIcon(props: ChatIconProps) {
  return chatGlyph(User, props)
}

export function ZapIcon(props: ChatIconProps) {
  return chatGlyph(Zap, props)
}

export function BellIcon(props: ChatIconProps) {
  return chatGlyph(Bell, props)
}

export function InfoIcon(props: ChatIconProps) {
  return chatGlyph(Info, props)
}

export function LockIcon(props: ChatIconProps) {
  return chatGlyph(Lock, props)
}

export function UploadIcon(props: ChatIconProps) {
  return chatGlyph(Upload, props)
}
